import type { ReactElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';

const waitForImages = async (container: HTMLElement): Promise<void> => {
  const images = Array.from(container.querySelectorAll('img'));
  const imagePromises = images.map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  });
  await Promise.all(imagePromises);
};

const waitForFonts = async (): Promise<void> => {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
};

const cloneElementForPrint = (element: HTMLElement): HTMLElement => {
  const clonedElement = element.cloneNode(true) as HTMLElement;

  const removeScrollConstraints = (el: HTMLElement) => {
    el.style.height = 'auto';
    el.style.maxHeight = 'none';

    el.style.overflow = 'visible';
    el.style.overflowY = 'visible';
    el.style.overflowX = 'visible';

    if (el.style.height.includes('calc(')) {
      el.style.height = 'auto';
    }

    Array.from(el.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        removeScrollConstraints(child);
      }
    });
  };

  removeScrollConstraints(clonedElement);

  const svgs = clonedElement.querySelectorAll('svg');
  svgs.forEach((svg) => {
    svg.removeAttribute('width');
    svg.removeAttribute('height');
  });

  return clonedElement;
};

const cloneStylesIntoIframe = (iframe: HTMLIFrameElement): void => {
  const iframeDoc = iframe.contentDocument;
  if (!iframeDoc) return;
  const iframeHead = iframeDoc.head;

  // Clone all stylesheets including CSS-in-JS injected styles
  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      // Handle external stylesheets (link tags)
      if (
        sheet.ownerNode &&
        sheet.ownerNode instanceof HTMLElement &&
        sheet.ownerNode.tagName === 'LINK'
      ) {
        const linkElement = sheet.ownerNode as HTMLLinkElement;
        const clonedLink = linkElement.cloneNode(true) as HTMLLinkElement;
        iframeHead.appendChild(clonedLink);
      }
      // Handle inline style tags
      else if (
        sheet.ownerNode &&
        sheet.ownerNode instanceof HTMLElement &&
        sheet.ownerNode.tagName === 'STYLE'
      ) {
        const styleElement = sheet.ownerNode as HTMLStyleElement;
        const clonedStyle = styleElement.cloneNode(true) as HTMLStyleElement;
        iframeHead.appendChild(clonedStyle);
      }
      // Handle CSS-in-JS injected stylesheets (no ownerNode)
      else if (sheet.cssRules && sheet.cssRules.length > 0) {
        const injectedStyle = document.createElement('style');
        injectedStyle.textContent = Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
        iframeHead.appendChild(injectedStyle);
      }
    } catch (error) {
      console.warn('Could not clone stylesheet:', error);
    }
  });

  const remainingStyleElements = document.querySelectorAll(
    'link[rel="stylesheet"]:not([data-cloned]), style:not([data-cloned])'
  );
  remainingStyleElements.forEach((el) => {
    const clonedEl = el.cloneNode(true) as HTMLElement;
    iframeHead.appendChild(clonedEl);
    el.setAttribute('data-cloned', 'true');
  });

  // Add print-specific styles directly
  const printStyles = document.createElement('style');
  printStyles.textContent = `
    @media print {
      @page {
        margin: 0;
        size: auto;
      }

      [data-cover-page="true"] {
        min-height: 100vh;
      }
    }
  `;

  iframeHead.appendChild(printStyles);
};

export const browserPrintComponent = async (
  content: ReactElement | HTMLElement
): Promise<void> => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';

  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) throw new Error('Failed to get iframe document');
    const iframeBody = iframeDoc.body;

    const targetWidth = '850px';
    iframeBody.style.width = targetWidth;
    iframeBody.style.minWidth = targetWidth;
    iframeBody.style.maxWidth = targetWidth;

    iframeDoc.head.innerHTML = '';
    iframeDoc.body.innerHTML = '';
    iframeBody.style.margin = '0';
    iframeBody.style.padding = '0';
    iframeBody.className = 'print-mode';

    // Clone styles into iframe
    cloneStylesIntoIframe(iframe);

    // Wait for stylesheets to load
    const styleSheets = Array.from(
      iframeDoc.querySelectorAll('link[rel="stylesheet"]')
    );
    await Promise.all(
      styleSheets.map(
        (link) =>
          new Promise<void>((resolve) => {
            if (link instanceof HTMLLinkElement) {
              if (link.sheet) {
                resolve();
              } else {
                link.onload = () => resolve();
                link.onerror = () => resolve();
              }
            } else {
              resolve();
            }
          })
      )
    );

    // Render content
    let root: Root | null = null;
    if (content instanceof HTMLElement) {
      const clonedElement = cloneElementForPrint(content);
      iframeBody.appendChild(clonedElement);
    } else {
      root = createRoot(iframeBody);
      root.render(content);
    }

    await new Promise((resolve) => {
      if (iframeDoc.readyState === 'complete') resolve(null);
      else iframe.onload = resolve;
    });

    if (iframeDoc.fonts?.ready) {
      await iframeDoc.fonts.ready;
    }

    await Promise.all([waitForFonts(), waitForImages(iframeBody)]);

    iframe.contentWindow?.print();

    const cleanup = () => {
      if (root) {
        root.unmount();
      }
      iframe.remove();
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup, { once: true });
  } catch (error) {
    console.error('Error in browserPrintComponent:', error);
    iframe.remove();
    throw error;
  }
};
